import { isLocale, type Locale } from '@/i18n/config'

/**
 * The visitor's explicit language preference. Written by the suggestion
 * banner and the locale switcher, read by the proxy to route returning
 * visitors from bare paths to their locale. Deliberately not httpOnly:
 * client components own the writes.
 */
export const PF_LOCALE_COOKIE = 'pf_locale'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/**
 * First entry of `languages` whose primary subtag is a candidate locale
 * (`de-AT` matches `de`). Order expresses the browser's preference ranking.
 */
export function matchBrowserLocale(
  languages: readonly string[] | undefined,
  candidates: readonly Locale[],
): Locale | null {
  if (!languages) return null
  for (const tag of languages) {
    const primary = tag.toLowerCase().split('-')[0]
    if (isLocale(primary) && candidates.includes(primary)) return primary
  }
  return null
}

/** Extract a supported locale from a `document.cookie`-style string. */
export function readLocaleCookie(cookieString: string): Locale | null {
  const match = cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${PF_LOCALE_COOKIE}=`))
  const value = match?.slice(PF_LOCALE_COOKIE.length + 1)
  return isLocale(value) ? value : null
}

/** Serialized Set-Cookie value for assigning to `document.cookie`. */
export function localeCookieString(locale: Locale, secure: boolean): string {
  const base = `${PF_LOCALE_COOKIE}=${locale}; Max-Age=${ONE_YEAR_SECONDS}; Path=/; SameSite=Lax`
  return secure ? `${base}; Secure` : base
}
